/**
 * sortable-geometry - Pure Reanimated worklet helpers for variable-height
 * drag-to-reorder math.
 *
 * Every row in the to-do list can have a different height (single vs multi-line
 * titles, cards with subtasks, focused cards that reveal "+ Add subtask"). These
 * helpers operate on a per-index `heights` array instead of a single fixed
 * height so the reorder feels precise regardless of row size.
 */

/** Sum of the heights of all rows before `index` (i.e. the row's top offset). */
export function topOffsetForIndex(heights: number[], index: number): number {
  'worklet';
  let top = 0;
  for (let i = 0; i < index && i < heights.length; i += 1) {
    top += heights[i];
  }
  return top;
}

/**
 * Index of the slot whose vertical band contains the dragged row's center.
 * Uses cumulative heights so passing a tall card advances the index by exactly
 * one slot, not by a fixed pixel guess.
 */
export function computeHoverIndex(
  activeIndex: number,
  dragY: number,
  heights: number[],
  itemCount: number,
): number {
  'worklet';
  if (activeIndex < 0 || itemCount === 0) {
    return activeIndex;
  }
  const activeHeight = heights[activeIndex] ?? 0;
  const activeCenter = topOffsetForIndex(heights, activeIndex) + dragY + activeHeight / 2;

  let accumulated = 0;
  for (let i = 0; i < itemCount; i += 1) {
    const rowHeight = heights[i] ?? 0;
    if (activeCenter < accumulated + rowHeight) {
      return i;
    }
    accumulated += rowHeight;
  }
  return itemCount - 1;
}

/**
 * Animated Y offset for a row at `index`.
 * The active row follows the finger; displaced neighbours move by the active
 * row's real height so they open/close the correct gap.
 */
export function sortableYOffset(
  index: number,
  activeIndex: number,
  hoverIndex: number,
  dragY: number,
  heights: number[],
): { yOffset: number; isActive: boolean } {
  'worklet';
  if (activeIndex === index) {
    return { yOffset: dragY, isActive: true };
  }
  if (activeIndex < 0) {
    return { yOffset: 0, isActive: false };
  }

  const activeHeight = heights[activeIndex] ?? 0;
  let displacement = 0;
  if (activeIndex < index && hoverIndex >= index) {
    displacement = -activeHeight;
  } else if (activeIndex > index && hoverIndex <= index) {
    displacement = activeHeight;
  }
  return { yOffset: displacement, isActive: false };
}
